import React, { useEffect, useRef, useState } from "react";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { useGlobalStore } from "../../api/globalStores";
import { observer } from "mobx-react";
import { EngagementPanel as EngagementPanelStore } from "../../api/engagementPanel";
import IconSpeaker from "../../Common/Icons/IconSpeaker";

const EP_TYPE_TO_READABLE: Record<string, string> = {
  MCQ: "mcq",
  FLASH_CARD: "flash card",
  POLL: "poll",
};

export interface ISlideDrawerItem {
  id: number;
  position: number;
  epType: "MCQ" | "FLASH_CARD" | "POLL";
  isInEditMode?: boolean;
  curEPanelID?: number;
  onClick?: React.MouseEventHandler<HTMLDivElement>;
}

export default observer(SlideDrawerItem);

function SlideDrawerItem(props: ISlideDrawerItem) {
  const globalStores = useGlobalStore();

  const preloadedPanel = globalStores.curModule.engagementPanelsLoaded.find(
    (p) => p.engagementPanel.id === props.id
  );

  const [ownEPanel] = useState<EngagementPanelStore>(
    () => new EngagementPanelStore()
  );
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({ id: props.id, disabled: !props.isInEditMode });

  const style = props.isInEditMode
    ? { transform: CSS.Transform.toString(transform), transition }
    : undefined;

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      if (!props.isInEditMode && preloadedPanel) {
        await ownEPanel.initMetadataOnly(preloadedPanel.engagementPanel);
      } else {
        await ownEPanel.init(props.id);
      }
      if (cancelled || !mountedRef.current) return;
      if (props.curEPanelID === props.id) {
        globalStores.curEPanel.initFromInput(ownEPanel);
      }
    };
    run();
    return () => {
      cancelled = true;
    };
  }, [props.id, props.isInEditMode]);

  useEffect(() => {
    if (props.curEPanelID === props.id) {
      globalStores.curEPanel.initFromInput(ownEPanel);
    }
  }, [props.curEPanelID]);

  const onClickOverride = (event: React.MouseEvent<HTMLDivElement>) => {
    // @ts-ignore
    props.onClick(event!);
  };

  const active = props.id === globalStores.curEPanel.engagementPanel.id;

  const ep = ownEPanel.engagementPanel;
  const urls = ownEPanel.assetUrls;
  const hasImageOptions = !!(
    urls.answerA ||
    urls.answerB ||
    urls.answerC ||
    urls.answerD
  );

  return (
    <div
      ref={props.isInEditMode ? setNodeRef : undefined}
      {...(props.isInEditMode ? attributes : {})}
      {...(props.isInEditMode ? listeners : {})}
      key={props.position}
      style={style}
      onClick={onClickOverride}
      className="gap-2.5 inline-flex flex-row items-start justify-start cursor-pointer"
    >
      <p className="font-poppins text-normal font-semibold text-white inline leading-[1.5] w-2">
        {props.position + 1}
      </p>
      <div
        className={` border-solid border-4 bg-brij-White rounded-[10px] w-[98px] aspect-9/18 overflow-clip relative flex justify-center items-center ${
          active && "border-brij-Blue"
        }`}
      >
        {/* Lightweight mini preview — uses direct URLs, no File objects in memory */}
        <div className="absolute inset-0 flex flex-col overflow-hidden pt-4 px-1 pb-1">
          {ep.qns && ep.qns.trim() !== "" && (
            <p className="text-[5px] text-brij-DarkGrey leading-tight line-clamp-3 font-poppins px-0.5 mb-0.5">
              {ep.qns}
            </p>
          )}

          {urls.question && (
            <MiniQuestionAsset key={urls.question} url={urls.question} />
          )}

          {ep.type !== "FLASH_CARD" && (
            <div
              className={`mt-auto ${
                hasImageOptions
                  ? "grid grid-cols-2 gap-0.5"
                  : "flex flex-col gap-0.5"
              }`}
            >
              {hasImageOptions
                ? ["A", "B", "C", "D"].map((letter) => (
                    <div
                      key={letter}
                      className="bg-brij-Neutral rounded-sm aspect-square flex items-center justify-center"
                    >
                      <span className="text-[4px] text-brij-Mud font-semibold">
                        {letter}
                      </span>
                    </div>
                  ))
                : [ep.optionA, ep.optionB, ep.optionC, ep.optionD].map(
                    (text, i) =>
                      text ? (
                        <div
                          key={i}
                          className="bg-brij-Neutral rounded-sm px-1 py-0.5"
                        >
                          <p className="text-[4px] text-brij-Mud font-medium line-clamp-1 leading-tight">
                            {String.fromCharCode(65 + i)}. {text}
                          </p>
                        </div>
                      ) : null
                  )}
            </div>
          )}
        </div>

        {/* Type badge overlay */}
        <div className="absolute inset-0">
          <div className="rounded-md p-1.5 gap-0.5 bg-brij-Neutral inline-flex flex-col items-center justify-center overflow-clip">
            <p className="font-poppins font-semibold text-brij-Mud inline text-[10px] leading-[1.5]">
              {EP_TYPE_TO_READABLE[props.epType]}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

function MiniQuestionAsset({ url }: { url: string }) {
  const fallbackUrl = url.replace(/\.webp$/, "");
  const [srcState, setSrcState] = useState<"primary" | "fallback" | "failed">(
    "primary"
  );

  if (srcState === "failed") {
    return (
      <div className="flex-1 min-h-0 flex items-center justify-center">
        <div className="w-5 h-5 rounded-full bg-brij-Peach fill-brij-Default p-1">
          <IconSpeaker />
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 min-h-0 overflow-hidden flex items-center justify-center">
      <img
        src={srcState === "primary" ? url : fallbackUrl}
        alt=""
        className="object-contain w-full h-full"
        loading="lazy"
        decoding="async"
        onError={() => {
          if (srcState === "primary") {
            setSrcState("fallback");
          } else {
            setSrcState("failed");
          }
        }}
      />
    </div>
  );
}
